/**
 * ECS World - manages entities, components, and systems
 */

import type { Entity } from '@/types';
import type { ComponentType } from './Component';

export interface System {
  update(world: World, deltaTime: number): void;
}

export class World {
  private nextEntityId: Entity = 1;
  private entities: Set<Entity> = new Set();
  private components: Map<ComponentType, Map<Entity, unknown>> = new Map();
  private systems: System[] = [];
  private entitiesToDestroy: Entity[] = [];

  createEntity(): Entity {
    const id = this.nextEntityId++;
    this.entities.add(id);
    return id;
  }

  destroyEntity(entity: Entity): void {
    this.entitiesToDestroy.push(entity);
  }

  private processDestroyQueue(): void {
    for (const entity of this.entitiesToDestroy) {
      this.entities.delete(entity);
      for (const componentStore of this.components.values()) {
        componentStore.delete(entity);
      }
    }
    this.entitiesToDestroy = [];
  }

  addComponent<T>(entity: Entity, type: ComponentType, component: T): void {
    if (!this.components.has(type)) {
      this.components.set(type, new Map());
    }
    this.components.get(type)!.set(entity, component);
  }

  removeComponent(entity: Entity, type: ComponentType): void {
    const store = this.components.get(type);
    if (store) {
      store.delete(entity);
    }
  }

  getComponent<T>(entity: Entity, type: ComponentType): T | undefined {
    const store = this.components.get(type);
    return store?.get(entity) as T | undefined;
  }

  hasComponent(entity: Entity, type: ComponentType): boolean {
    const store = this.components.get(type);
    return store?.has(entity) ?? false;
  }

  query(componentTypes: ComponentType[]): Entity[] {
    const results: Entity[] = [];

    for (const entity of this.entities) {
      let hasAll = true;
      for (const type of componentTypes) {
        if (!this.hasComponent(entity, type)) {
          hasAll = false;
          break;
        }
      }
      if (hasAll) {
        results.push(entity);
      }
    }

    return results;
  }

  queryWithComponents<T extends Record<string, unknown>>(
    componentTypes: ComponentType[]
  ): { entity: Entity; components: T }[] {
    const results: { entity: Entity; components: T }[] = [];

    for (const entity of this.entities) {
      let hasAll = true;
      const components: Record<string, unknown> = {};

      for (const type of componentTypes) {
        const component = this.getComponent(entity, type);
        if (component === undefined) {
          hasAll = false;
          break;
        }
        components[type] = component;
      }

      if (hasAll) {
        results.push({ entity, components: components as T });
      }
    }

    return results;
  }

  getAllComponents<T>(type: ComponentType): Map<Entity, T> {
    const store = this.components.get(type);
    return (store as Map<Entity, T>) ?? new Map();
  }

  addSystem(system: System): void {
    this.systems.push(system);
  }

  update(deltaTime: number): void {
    for (const system of this.systems) {
      system.update(this, deltaTime);
    }
    this.processDestroyQueue();
  }

  getEntityCount(): number {
    return this.entities.size;
  }

  clear(): void {
    this.entities.clear();
    this.components.clear();
    this.entitiesToDestroy = [];
    this.nextEntityId = 1;
  }
}
